/**
 * How long a sign-in lasts, and how long a password does.
 *
 * Both are policy rather than mechanism, so both live here where they can be
 * read in one place and tested without a database or a clock.
 */
/**
 * A session lasts a working day and then ends. No idle timeout.
 *
 * There used to be one, at ninety minutes, and the reasoning for it was sound:
 * a console left open on a shared office laptop at ten and found by somebody
 * else at four is a real exposure, and an abandoned screen closing itself is
 * the cheapest possible mitigation.
 *
 * It was removed because it did not survive contact with the work. A dispatcher
 * spends the morning on the phone and the afternoon on the board; a technician
 * opens the app between jobs. Ninety minutes of "idle" is an ordinary gap in
 * this business, not an abandoned screen, and being signed out four times a day
 * teaches people to keep a password in a browser — which is a worse exposure
 * than the one the timeout was guarding against.
 *
 * **So the exposure is real and accepted, not overlooked.** The mitigations that
 * remain: twelve hours is bounded, every session is revoked on a password
 * change or reset, an admin can end them from the Users screen, and the console
 * signs itself out at the deadline rather than waiting to be refused.
 *
 * **Since ADR-038 there is an idle limit again — of a different kind.** It
 * counts time with *no request at all* from the session, and an open console
 * asks for news every minute, so it does not fire on a dispatcher who is on
 * the phone with the board on screen. It is four hours (two for an admin), and
 * it ends sessions on machines that have been shut or abandoned. See
 * `session-security.ts`.
 */
export const SESSION_HOURS = 12;
/**
 * How long a password may be kept.
 *
 * Ninety days from when it was chosen. For a new account that is ninety days
 * from first sign-in, because choosing a password *is* the first sign-in — the
 * handover password an admin types has to be replaced before the account works,
 * so `passwordChangedAt` and "first login" are the same moment.
 */
export const PASSWORD_MAX_AGE_DAYS = 90;
/** Days before expiry that the console starts saying so. */
export const PASSWORD_WARNING_DAYS = 10;
const DAY_MS = 86_400_000;
/**
 * Where this account stands on password age.
 *
 * `changedAt` null means the password has never been chosen by its owner — an
 * account still holding the handover password an admin typed. That is already
 * handled by `mustChangePassword`, and answering "expired" here as well would be
 * telling the same person the same thing twice in two different words.
 */
export function passwordStanding(account, now = new Date()) {
    if (!account.hasPassword)
        return { state: 'not-applicable' };
    if (!account.passwordChangedAt)
        return { state: 'fine', daysLeft: PASSWORD_MAX_AGE_DAYS };
    const ageDays = Math.floor((now.getTime() - account.passwordChangedAt.getTime()) / DAY_MS);
    const daysLeft = PASSWORD_MAX_AGE_DAYS - ageDays;
    /*
     * `Math.abs`, not `-daysLeft`. Negating zero in JavaScript gives -0, which
     * compares equal to 0 but renders as "-0" — so on the exact day a password
     * expired, somebody would have read "expired -0 days ago".
     */
    if (daysLeft <= 0)
        return { state: 'expired', daysOverdue: Math.abs(daysLeft) };
    if (daysLeft <= PASSWORD_WARNING_DAYS)
        return { state: 'expiring', daysLeft };
    return { state: 'fine', daysLeft };
}
/**
 * The sentence somebody reads about it.
 *
 * Written here rather than in the console so the API and the UI cannot describe
 * the same state differently — and so it says what to do, not merely what is
 * wrong. "Your password has expired" leaves somebody looking for a button;
 * naming the screen does not.
 */
export function passwordStandingMessage(standing) {
    switch (standing.state) {
        case 'expired':
            return standing.daysOverdue === 0
                ? 'Your password is ninety days old and has to be changed now. Choose a new one to carry on.'
                : `Your password expired ${standing.daysOverdue} ${standing.daysOverdue === 1 ? 'day' : 'days'} ago. Choose a new one to carry on — nothing else will open until you do.`;
        case 'expiring':
            return standing.daysLeft === 1
                ? 'Your password has to be changed tomorrow. You can do it now from Your account.'
                : `Your password has to be changed in ${standing.daysLeft} days. You can do it now from Your account.`;
        default:
            return null;
    }
}
/**
 * How long a session lasts when somebody asks to stay signed in.
 *
 * Thirty days, and offered because the twelve-hour default was being worked
 * around rather than accepted. A technician signing in on their own handset
 * every single morning learns to keep the password in the browser, or picks a
 * shorter one, or writes it down — each of which is a worse exposure than
 * the longer session it was avoiding.
 *
 * **The exposure is real and accepted, not overlooked.** It is a deliberate
 * choice, made per device, by the person who owns that device. Everything that
 * ends a session still ends this one: a password change or reset revokes every
 * session the account has, an admin can end them from the Users screen, and
 * signing out ends it immediately. What it is *not* suitable for is a shared
 * machine, which is why the console says so beside the box rather than in a
 * help page nobody opens.
 */
export const REMEMBERED_DAYS = 30;
/**
 * When a session opened now would end.
 *
 * `remembered` is what the person ticked, not a setting — the same account
 * can hold a thirty-day session on a personal handset and a twelve-hour one on
 * the office machine, which is exactly the distinction that makes the long one
 * defensible.
 */
export function sessionExpiry(from = new Date(), remembered = false) {
    const hours = remembered ? REMEMBERED_DAYS * 24 : SESSION_HOURS;
    return new Date(from.getTime() + hours * 3600_000);
}
/**
 * Milliseconds until a session ends, floored at zero.
 *
 * The console counts down on this so it can sign itself out at the deadline
 * instead of discovering it on the next click — which is the difference between
 * being told your day has ended and watching a page fail to load.
 */
export function millisecondsLeft(expiresAt, now = new Date()) {
    return Math.max(0, expiresAt.getTime() - now.getTime());
}
//# sourceMappingURL=sessions.js.map